import { useState, useRef, useEffect, useMemo } from "react";
import { format, endOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";

export type Period = "7d" | "30d" | "90d" | "12m" | "all" | "custom";

export interface DateRangeSelectorProps {
  value: Period;
  onChange: (period: Period) => void;
  customRange?: { from: Date; to: Date };
  onCustomRangeChange?: (range: { from: Date; to: Date } | undefined) => void;
  className?: string;
}

const PERIODS: { value: Exclude<Period, "custom">; label: string }[] = [
  { value: "7d", label: "7 dagar" },
  { value: "30d", label: "30 dagar" },
  { value: "90d", label: "90 dagar" },
  { value: "12m", label: "12 mån" },
  { value: "all", label: "Allt" },
];

export function DateRangeSelector({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
  className,
}: DateRangeSelectorProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    customRange ? { from: customRange.from, to: customRange.to } : undefined
  );
  const calendarRef = useRef<HTMLDivElement>(null);
  // Stable "end of today" so rdp never disables today itself due to time-of-day mismatch
  const maxDate = useMemo(() => endOfDay(new Date()), []);

  // Prevent clicks inside the calendar panel from propagating to the
  // document (which would hit the backdrop and close the panel).
  // We use a native listener so it fires before React's delegation.
  useEffect(() => {
    const el = calendarRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener("mousedown", stop, true);
    return () => el.removeEventListener("mousedown", stop, true);
  });

  // Close on Escape
  useEffect(() => {
    if (!isCalendarOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsCalendarOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isCalendarOpen]);

  const handlePresetClick = (period: Exclude<Period, "custom">) => {
    onChange(period);
    setDateRange(undefined);
    setIsCalendarOpen(false);
  };

  const handleDateSelect = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      onChange("custom");
      onCustomRangeChange?.({ from: range.from, to: range.to });
      setIsCalendarOpen(false);
    }
  };

  const formatDateRange = () => {
    if (!dateRange?.from) return "Välj datum";
    if (!dateRange.to)
      return format(dateRange.from, "d MMM yyyy", { locale: sv });
    return `${format(dateRange.from, "d MMM", { locale: sv })} – ${format(dateRange.to, "d MMM yyyy", { locale: sv })}`;
  };

  return (
    <>
      {/* Full-screen invisible backdrop — closes calendar on outside click */}
      {isCalendarOpen && (
        <div
          className="fixed inset-0 z-40"
          onMouseDown={() => setIsCalendarOpen(false)}
        />
      )}

      <div
        className={cn(
          "relative z-[45] inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1 gap-0.5",
          className
        )}
      >
        {PERIODS.map((period) => (
          <button
            key={period.value}
            onClick={() => handlePresetClick(period.value)}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
              value === period.value
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            {period.label}
          </button>
        ))}

        <div className="relative">
          <button
            onClick={() => {
              if (!isCalendarOpen) {
                // Reset range so user always starts a fresh selection
                setDateRange(undefined);
              }
              setIsCalendarOpen(!isCalendarOpen);
            }}
            className={cn(
              "px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5",
              value === "custom"
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            )}
          >
            <CalendarIcon className="h-4 w-4" />
            {value === "custom" && dateRange?.from && dateRange?.to ? (
              <span className="hidden sm:inline">{formatDateRange()}</span>
            ) : (
              <span className="hidden sm:inline">Anpassat</span>
            )}
          </button>

          {isCalendarOpen && (
            <div
              ref={calendarRef}
              className="absolute right-0 top-full z-50 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg"
            >
              <Calendar
                mode="range"
                min={1}
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={handleDateSelect}
                numberOfMonths={2}
                disabled={{ after: maxDate }}
              />
              <div className="border-t border-gray-100 p-3 flex justify-between items-center">
                <p className="text-sm text-gray-500">
                  {dateRange?.from && dateRange?.to
                    ? formatDateRange()
                    : "Välj start- och slutdatum"}
                </p>
                {dateRange?.from && dateRange?.to && (
                  <button
                    onClick={() => {
                      setDateRange(undefined);
                      onCustomRangeChange?.(undefined);
                      onChange("30d");
                      setIsCalendarOpen(false);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Rensa
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
